import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';

export default function Payout({ teacherId }) {
  const [monthlyClasses, setMonthlyClasses] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMonthlyClasses();
  }, [teacherId]);

  const fetchMonthlyClasses = async () => {
    try {
      setLoading(true);
      const now = new Date();
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);

      const { data, error } = await supabase
        .from('individual_classes')
        .select('*')
        .eq('teacher_id', teacherId)
        .gte('class_date', monthStart.toISOString())
        .lte('class_date', monthEnd.toISOString())
        .order('class_date', { ascending: true });

      if (error) throw error;

      setMonthlyClasses(data || []);
      calculateTotalEarnings(data);
    } catch (error) {
      console.error('Error fetching monthly classes:', error.message);
      setError('Failed to fetch monthly classes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalEarnings = (classes) => {
    const total = classes.reduce((sum, classItem) => sum + (classItem.is_completed ? 150 : 0), 0);
    setTotalEarnings(total);
  };

  const markClassAsCompleted = async (classId) => {
    try {
      const { data, error } = await supabase
        .from('individual_classes')
        .update({ is_completed: true })
        .eq('id', classId);

      if (error) throw error;

      // Update the local state
      setMonthlyClasses(prevClasses => 
        prevClasses.map(c => c.id === classId ? { ...c, is_completed: true } : c)
      );
      calculateTotalEarnings(monthlyClasses);
    } catch (error) {
      console.error('Error marking class as completed:', error.message);
      setError('Failed to mark class as completed. Please try again.');
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <h2 className="text-2xl font-semibold mb-4">Monthly Payout</h2>
      <p className="text-xl mb-4">Total Earnings: ₹{totalEarnings}</p>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {monthlyClasses.map((classItem) => (
              <tr key={classItem.id}>
                <td className="px-6 py-4 whitespace-nowrap">{format(parseISO(classItem.class_date), 'MMM d, yyyy')}</td>
                <td className="px-6 py-4 whitespace-nowrap">{classItem.class_name}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {classItem.is_completed ? 'Completed' : 'Pending'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {!classItem.is_completed && (
                    <button
                      onClick={() => markClassAsCompleted(classItem.id)}
                      className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                    >
                      Mark as Completed
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}